/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// part-geometry.js — where things SIT on the desk, in world (pitch) units, and
// what a pointer is over. Pure geometry over a document's boards + components:
// resolving pin/terminal/address positions and hit-testing them. The desk
// controller was the only home for this maths; pulling it here makes it
// independently testable and keeps the controller a thin view of it.
//
// Everything takes plain arrays (a board is `{id,type,x,y,rot}`, a component
// `{id,kind,ref,board,anchor,params,x,y}`), so a test builds a scene as data.
// A missing/unresolvable part contributes nothing — it never throws.

// `tf`, not `t`: this is model code, read under `node --test` with no catalog
// loaded, so every string carries its English along (the build-plan.js rule —
// see catalog/labels.js).
import { tf } from "../i18n.js";
import { formatAddress, holePosition, parseAddress } from "./breadboard.js";
// holePosition drives partPinsWorld below; addressWorld uses the canonical
// worldOfAddress from occupancy.
import {
  holeAtWorld,
  holesNearWorld,
  partCoverAddresses,
  partPinAddresses,
  partPinHoles,
  worldOfAddress,
} from "./occupancy.js";
import { boardRect } from "./mating.js";
import { partDef } from "../catalog/index.js";

/** How close (pitch units) a point must be to a pin/terminal to hit it. */
export const PIN_HIT_RADIUS = 0.45;

/** How close the cursor must come to a wire's end cap to grab it. */
export const WIRE_END_GRAB_RADIUS = 0.6;

/** How close it must come to a ROUTED wire's waypoint to grab THAT — a little
    wider than an end cap, since a waypoint has no hole under it to aim at. */
export const WIRE_POINT_GRAB_RADIUS = 0.75;

/** How close a dragged point must be dropped to another point of the same wire
    (a waypoint, or either end) to MERGE into it — the gesture that takes a bend
    back out. Wider than the grab radius on purpose: merging is what a user
    aiming "back onto the line" means, and a near miss that silently left a
    waypoint a third of a hole away from its neighbour would read as a bug. */
export const WIRE_POINT_MERGE_RADIUS = 1.1;

/**
 * Every pin of a seated board part as `{ pin, address, x, y }` — the world
 * position each lead sits at (a bent lead by where it lands, floating or not)
 * plus the address it resolves to (null when floating). Null when the part
 * doesn't resolve. `boards` may carry a moved origin for a live board drag.
 */
export function partPinsWorld(boards, comp) {
  const board = boards.find((b) => b.id === comp.board);
  const pins = board && partPinHoles(comp.ref, comp.anchor, comp.params);
  if (!pins) return null;
  const addressed = partPinAddresses({ boards }, comp);
  if (!addressed) return null;
  // Hole ids are stated in the strip's own unrotated frame, so every
  // holePosition here needs the strip's angle — exactly as partPinAddresses
  // resolves it. Without it the drawn pins and the addressed ones disagree.
  const rot = board.rot ?? 0;
  const anchorPos = holePosition(board.type, comp.anchor, rot);
  if (!anchorPos) return null;
  const out = [];
  for (const [i, { pin, hole, offset }] of pins.entries()) {
    const address = addressed[i].address;
    if (offset) {
      out.push({
        pin,
        address,
        x: board.x + anchorPos.x + offset.dx,
        y: board.y + anchorPos.y + offset.dy,
      });
      continue;
    }
    // A seated pin lives in a hole of its own board — no hole, no geometry.
    const pos = holePosition(board.type, hole, rot);
    if (!pos) return null;
    out.push({ pin, address, x: board.x + pos.x, y: board.y + pos.y });
  }
  return out;
}

/**
 * The world position of an address — a board hole (via the canonical,
 * rotation-aware worldOfAddress) or a brick terminal.
 */
export function addressWorld(boards, components, address) {
  const hole = worldOfAddress(boards, address);
  if (hole) return hole;
  const parsed = parseAddress(address);
  const comp = parsed && components.find((c) => c.id === parsed.boardId);
  const t = partDef(comp?.ref)?.terminals?.find((x) => x.id === parsed.hole);
  return t ? { x: comp.x + t.dx, y: comp.y + t.dy } : null;
}

/**
 * The nearest connection point (board hole or brick terminal) to a world
 * point, as `{ address, x, y }`, or null over nothing. Holes win outright
 * (via holeAtWorld); terminals match within PIN_HIT_RADIUS.
 */
export function connectionPointAt(boards, components, world) {
  const hole = holeAtWorld(boards, world.x, world.y);
  if (hole) {
    return {
      address: formatAddress(hole.board.id, hole.hole),
      x: hole.x,
      y: hole.y,
    };
  }
  for (const comp of components) {
    const terminals = partDef(comp.ref)?.terminals;
    if (!terminals) continue;
    for (const t of terminals) {
      const x = comp.x + t.dx;
      const y = comp.y + t.dy;
      if (Math.hypot(world.x - x, world.y - y) > PIN_HIT_RADIUS) continue;
      return { address: formatAddress(comp.id, t.id), x, y };
    }
  }
  return null;
}

/** How close (pitch units) a legal point must be for a dragged SINGLE POINT —
    a wire's end, a two-terminal part's lead (or its pin 1, carrying the body),
    a signal flag's or an Output/Input tag's apex — to snap onto it; further
    than that it rides the cursor, and a release there reverts. 1.2
    reaches the orthogonal neighbours of the hole under the cursor (1 pitch)
    but not its diagonals (1.41), so a near-miss is forgiven by ONE hole and
    never more, and the channel's midline (1.5 from rows e and f) snaps to
    nothing.

    The candidates are the REAL points around the cursor (connectionPointsNear),
    nearest first. A wire end used to try whole-pitch offsets from the raw
    cursor instead, and a cursor is not on the lattice: half a pitch off a
    column, every sample fell between the holes, the board under it went
    invisible, and the unbounded search a release fell back to walked on until
    it met a strip on ANOTHER lattice (a turned rail's holes sit on quarters) —
    an end dropped beside f1 landed on a rail strip nearly five pitches away.
    The preview asks the one bounded question (nearestLegalPoint), and the
    drop commits its answer as it stands — never re-asked at the release
    point, so the drop is always the ring that was on screen. */
export const END_SNAP_RADIUS = 1.2;

/** Distances closer than this are a TIE: a hole one row up and one a column
    along are both "1 pitch" away, and floating point must not pick between
    them — the stated tie-break does. */
const DIST_EPS = 1e-9;

/**
 * Every connection point (board hole or brick terminal) within `radius` of a
 * world point, nearest first, as `[{ address, x, y, dist }]` — the widened
 * `connectionPointAt`. A drag walks it in order and lands on the first point
 * it may, so the answer is always the NEAREST legal point and never one a
 * stride of samples happened to hit first (see `holesNear`).
 *
 * Ties go top-to-bottom, then left-to-right — the order `ringOffsets`
 * documents — so the same cursor always picks the same hole.
 */
export function connectionPointsNear(boards, components, world, radius) {
  const out = holesNearWorld(boards, world.x, world.y, radius).map((h) => ({
    address: formatAddress(h.board.id, h.hole),
    x: h.x,
    y: h.y,
    dist: h.dist,
  }));
  for (const comp of components) {
    const terminals = partDef(comp.ref)?.terminals;
    if (!terminals) continue;
    for (const t of terminals) {
      const x = comp.x + t.dx;
      const y = comp.y + t.dy;
      const dist = Math.hypot(world.x - x, world.y - y);
      if (dist > radius) continue;
      out.push({ address: formatAddress(comp.id, t.id), x, y, dist });
    }
  }
  return out.sort((a, b) =>
    Math.abs(a.dist - b.dist) > DIST_EPS
      ? a.dist - b.dist
      : a.y - b.y || a.x - b.x,
  );
}

/**
 * Where a drag aiming ONE point at `world` lands: the nearest connection point
 * within `radius` that `accept` takes — the point under the cursor when it is
 * legal, else a neighbour one hole along, the way a magnet-snapped connector
 * forgives a near-miss. Every single-point drag asks this at every sample, and
 * its release commits the last answer (never re-asking at the release point).
 *
 * `accept(point)` returns anything truthy to take a point, and that value comes
 * back as `accepted` — a drag whose legality check already derives the seat it
 * would commit (a lead's bend, a body's rider plan) keeps it rather than
 * deriving it twice. `inReach` says whether ANY point lay within reach, which
 * is how a flag tells a MISS (aimed at a board, nothing there free) from BARE
 * DESK (the unplug). Pass no components for a drag that lands on board holes
 * only.
 *
 * @returns {{point: {address:string,x:number,y:number,dist:number}|null,
 *   accepted: any, inReach: boolean}}
 */
export function nearestLegalPoint(
  boards,
  components,
  world,
  accept,
  radius = END_SNAP_RADIUS,
) {
  const near = connectionPointsNear(boards, components ?? [], world, radius);
  for (const point of near) {
    const accepted = accept(point);
    if (accepted) return { point, accepted, inReach: true };
  }
  return { point: null, accepted: null, inReach: near.length > 0 };
}

/**
 * World positions of a component's connection points: a board part's derived
 * pin holes, or a desk brick's terminals. Empty when they don't resolve — an
 * unresolvable part is never marquee-selectable.
 */
export function componentPoints(boards, comp) {
  const def = partDef(comp.ref);
  if (!def) return [];
  if (comp.board == null) {
    return (def.terminals ?? []).map((t) => ({
      x: comp.x + t.dx,
      y: comp.y + t.dy,
    }));
  }
  // A bent lead counts by where it SITS, floating or not — the marquee
  // encloses what the user can see.
  const pins = partPinsWorld(boards, comp);
  return pins ? pins.map(({ x, y }) => ({ x, y })) : [];
}

const inRect = (rect, p) =>
  p &&
  p.x >= rect.minX &&
  p.x <= rect.maxX &&
  p.y >= rect.minY &&
  p.y <= rect.maxY;

/**
 * Ids of components lying WHOLLY inside the world rect — the rubber band's
 * part pick. Enclosure, never a graze (CLAUDE.md → "Selection"): every
 * pin/terminal must be inside, and so must the drawn BODY — a desk brick's
 * own box, and a seated part's when `bodyBox` can say where it is
 * (`components/part-body-box.js`: a box local to the anchor hole, or null to
 * go by the pins alone). It is an argument because that geometry belongs to
 * the views, which a model module may not import.
 *
 * @param {Array} boards
 * @param {Array} components
 * @param {{minX:number, minY:number, maxX:number, maxY:number}} rect
 * @param {((comp: object) => ({minX:number, minY:number, width:number,
 *   height:number}|null))|null} [bodyBox]
 */
export function componentsInRect(boards, components, rect, bodyBox = null) {
  const ids = [];
  for (const comp of components) {
    const points = componentPoints(boards, comp);
    if (points.length === 0 || !points.every((p) => inRect(rect, p))) continue;
    if (!bodyInRect(boards, comp, rect, bodyBox)) continue;
    ids.push(comp.id);
  }
  return ids;
}

/** Whether a component's drawn body (where it can be known) is inside. */
function bodyInRect(boards, comp, rect, bodyBox) {
  if (comp.board == null) {
    const size = partDef(comp.ref)?.size;
    if (!size) return true;
    return (
      inRect(rect, { x: comp.x, y: comp.y }) &&
      inRect(rect, { x: comp.x + size.width, y: comp.y + size.height })
    );
  }
  const box = bodyBox?.(comp);
  if (!box) return true;
  const anchor = addressWorld(boards, [], `${comp.board}.${comp.anchor}`);
  if (!anchor) return true;
  const x = anchor.x + box.minX;
  const y = anchor.y + box.minY;
  return (
    inRect(rect, { x, y }) &&
    inRect(rect, { x: x + box.width, y: y + box.height })
  );
}

/**
 * Ids of boards lying WHOLLY inside the world rect — the marquee's board pick
 * (Feature 240). Whole-rect, to match the component rule ("every pin inside"):
 * a design is copied by drawing a box around it, and a board only half in the
 * box was not part of what the user drew around.
 */
export function boardsInRect(boards, rect) {
  return boards
    .filter((b) => {
      const r = boardRect(b);
      return (
        inRect(rect, { x: r.x, y: r.y }) &&
        inRect(rect, { x: r.x + r.width, y: r.y + r.height })
      );
    })
    .map((b) => b.id);
}

/** Ids of wires lying wholly inside the world rect: BOTH endpoints, and
    every bend of a routed one. */
export function wiresInRect(boards, components, wires, rect) {
  return wires
    .filter(
      (w) =>
        inRect(rect, addressWorld(boards, components, w.from)) &&
        inRect(rect, addressWorld(boards, components, w.to)) &&
        (w.points ?? []).every((p) => inRect(rect, p)),
    )
    .map((w) => w.id);
}

/**
 * The bounding rect (world units) of everything on the desk — every board,
 * every component's pins/terminals, and every wire endpoint. Null when the
 * desk is empty, so a camera "fit" has nothing to frame.
 */
export function deskBounds(boards, components, wires) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const grow = (x, y) => {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  };
  for (const board of boards) {
    const r = boardRect(board);
    grow(r.x, r.y);
    grow(r.x + r.width, r.y + r.height);
  }
  for (const comp of components) {
    for (const p of componentPoints(boards, comp)) grow(p.x, p.y);
  }
  for (const wire of wires) {
    const a = addressWorld(boards, components, wire.from);
    const b = addressWorld(boards, components, wire.to);
    if (a) grow(a.x, a.y);
    if (b) grow(b.x, b.y);
  }
  return Number.isFinite(minX) ? { minX, minY, maxX, maxY } : null;
}

/**
 * The nearest wire endpoint within WIRE_END_GRAB_RADIUS of a world point, as
 * `{ wireId, end, origin, dist }`, or null — how a cap-grab drag begins.
 */
export function wireEndNear(boards, components, wires, world) {
  let best = null;
  for (const wire of wires) {
    for (const end of ["from", "to"]) {
      const p = addressWorld(boards, components, wire[end]);
      if (!p) continue;
      const dist = Math.hypot(world.x - p.x, world.y - p.y);
      if (dist > WIRE_END_GRAB_RADIUS) continue;
      if (!best || dist < best.dist) {
        best = { wireId: wire.id, end, origin: wire[end], dist };
      }
    }
  }
  return best;
}

/**
 * The nearest ROUTED wire's waypoint within `radius` of a world point, as
 * `{ wireId, index, origin, dist }`, or null — how a waypoint drag begins.
 * A direct wire has no waypoints, so it can never answer here.
 *
 * Waypoints are DESK coordinates (the one part of a wire that isn't an
 * address), so this needs neither boards nor components — only the wires.
 */
export function wirePointNear(wires, world, radius = WIRE_POINT_GRAB_RADIUS) {
  let best = null;
  for (const wire of wires) {
    if (wire.layout !== "routed") continue;
    (wire.points ?? []).forEach((p, index) => {
      const dist = Math.hypot(world.x - p.x, world.y - p.y);
      if (dist > radius) return;
      if (!best || dist < best.dist) {
        best = { wireId: wire.id, index, origin: { ...p }, dist };
      }
    });
  }
  return best;
}

/**
 * What a hover is over: a part pin/terminal (they sit above), else a bare
 * hole, as `{ key, label, address, x, y }`, or null. `key` is a stable hover
 * identity; `address` is the conductive point (null for a floating lead).
 *
 * A hole under a chip's BODY (rows e–g beneath a 600-mil part at its true
 * width — occupancy.js `partCoverAddresses`) is not offered: the plastic is
 * over it, so nothing on the desk can reach it, and a ring there would point
 * at a hole no lead may use.
 */
export function hoverHitAt(boards, components, world) {
  const pin = pinHitAt(boards, components, world);
  if (pin) return pin;
  const hole = holeAtWorld(boards, world.x, world.y);
  if (!hole) return null;
  const address = formatAddress(hole.board.id, hole.hole);
  const docLike = { boards };
  if (
    components.some((c) => partCoverAddresses(docLike, c).includes(address))
  ) {
    return null;
  }
  return { key: address, label: address, address, x: hole.x, y: hole.y };
}

function pinHitAt(boards, components, world) {
  for (const comp of components) {
    const def = partDef(comp.ref);
    // Desk-level bricks (PSU, clock) expose terminals as connection points.
    if (comp.board == null && def?.terminals) {
      for (const t of def.terminals) {
        const x = comp.x + t.dx;
        const y = comp.y + t.dy;
        if (Math.hypot(world.x - x, world.y - y) > PIN_HIT_RADIUS) continue;
        const address = formatAddress(comp.id, t.id);
        // What this terminal IS, in the reader's own language — the address
        // beside it already says what it is CALLED. A voltage keeps its unit
        // symbol, which is the same in all seven.
        let note = null;
        if (comp.kind === "psu") {
          note =
            t.id === "+"
              ? tf("desk.hover.psuPlus", "+{volts} V", {
                  volts: comp.params.volts,
                })
              : tf("desk.hover.psuMinus", "0 V");
        } else if (comp.kind === "clock") {
          note =
            t.id === "out"
              ? tf("desk.hover.clockOut", "clock out")
              : t.id === "vcc"
                ? tf("desk.hover.clockVcc", "supply")
                : tf("desk.hover.clockGnd", "gnd");
        } else if (comp.kind === "load") {
          note =
            t.id === "pos"
              ? tf("desk.hover.loadPos", "load +")
              : tf("desk.hover.loadNeg", "load −");
        } else if (comp.kind === "ac") {
          note =
            t.id === "L"
              ? tf("desk.hover.acLine", "AC line")
              : tf("desk.hover.acNeutral", "AC neutral · 0 V");
        } else if (comp.kind === "adapter") {
          note = tf("desk.hover.adapterOutput", "output · isolated");
        } else if (comp.kind === "transformer") {
          note = t.id.startsWith("P")
            ? tf("desk.hover.transformerPrimary", "primary")
            : tf("desk.hover.transformerSecondary", "secondary");
        }
        return {
          key: `${comp.id}#${t.id}`,
          label: note ? `${address} · ${note}` : address,
          address,
          x,
          y,
        };
      }
      continue;
    }
    const pins = partPinsWorld(boards, comp);
    if (!pins) continue;
    for (const { pin, address, x, y } of pins) {
      if (Math.hypot(world.x - x, world.y - y) > PIN_HIT_RADIUS) continue;
      const name = def.pins.find((p) => p.n === pin)?.name ?? "?";
      return {
        key: `${comp.id}#${pin}`,
        // The ref and the pin NAME are the part's own identifiers and stay as
        // the catalog spells them; the sentence around them is the desk's.
        label: tf("desk.hover.pin", "{ref} pin {pin} · {name} → {address}", {
          ref: comp.ref,
          pin,
          name,
          // A floating lead is still hoverable — it just has no net to name.
          address: address ?? tf("desk.hover.floating", "floating"),
        }),
        address, // a pin resolves to the net of its seated hole
        x,
        y,
      };
    }
  }
  return null;
}
